import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Html5Qrcode } from "html5-qrcode";

/**
 * BarcodeScanner — Camera-based barcode scanner + manual entry input.
 *
 * Props:
 *   onBarcodeDetected(barcode: string) — called when a barcode is scanned or entered manually
 *   onClose() — dismiss the scanner
 *   onModeChange(mode: string) — called when internal mode changes ("choose" | "camera" | "manual")
 *   loading — if true, shows a loading state on the manual entry button
 */
export default function BarcodeScanner({ onBarcodeDetected, onClose, onModeChange, loading }) {
  const [mode, setMode] = useState("choose"); // "choose" | "camera" | "manual"
  const [manualCode, setManualCode] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);
  const streamRef = useRef(null); // Store the pre-acquired media stream

  // Notify parent of mode changes
  useEffect(() => {
    if (onModeChange) onModeChange(mode);
  }, [mode, onModeChange]);

  // ── Stop camera scanner ────────────────────────────────────────────────────
  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        // Only stop if currently scanning (state 2 = SCANNING)
        if (state === 2) {
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (e) {
        // Ignore cleanup errors
      }
      scannerRef.current = null;
    }
    // Stop all tracks on the pre-acquired stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  // ── Start camera scanner (called AFTER permission is granted & DOM ready) ──
  const startScanner = useCallback(async () => {
    setCameraError("");
    setScanning(true);

    try {
      const html5QrCode = new Html5Qrcode("barcode-reader");
      scannerRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 280, height: 150 },
        aspectRatio: 1.0,
        formatsToSupport: [
          0,  // QR_CODE
          1,  // AZTEC
          2,  // CODABAR
          3,  // CODE_39
          4,  // CODE_93
          5,  // CODE_128
          6,  // DATA_MATRIX
          7,  // MAXICODE
          8,  // ITF
          9,  // EAN_13
          10, // EAN_8
          11, // PDF_417
          12, // RSS_14
          13, // RSS_EXPANDED
          14, // UPC_A
          15, // UPC_E
          16, // UPC_EAN_EXTENSION
        ],
      };

      // Try to get a camera ID for the back camera for best compatibility
      let cameraId = null;
      try {
        const devices = await Html5Qrcode.getCameras();
        if (devices && devices.length > 0) {
          // Prefer back/environment camera
          const backCam = devices.find(
            (d) =>
              d.label.toLowerCase().includes("back") ||
              d.label.toLowerCase().includes("rear") ||
              d.label.toLowerCase().includes("environment")
          );
          cameraId = backCam ? backCam.id : devices[devices.length - 1].id;
        }
      } catch (enumErr) {
        // Camera enumeration failed — we'll fall back to facingMode constraint
        console.warn("[BarcodeScanner] Could not enumerate cameras:", enumErr);
      }

      // Use cameraId if available (more reliable on mobile), otherwise facingMode
      const cameraConfig = cameraId
        ? cameraId
        : { facingMode: "environment" };

      await html5QrCode.start(
        cameraConfig,
        config,
        (decodedText) => {
          // Barcode detected!
          stopScanner();
          onBarcodeDetected(decodedText);
        },
        () => {} // ignore scan failures (just means no barcode in frame yet)
      );

      // html5-qrcode has opened its own stream — stop our pre-acquired one
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    } catch (err) {
      console.error("[BarcodeScanner] Camera error:", err);
      setScanning(false);

      const errStr = err?.toString?.() || "";
      if (errStr.includes("NotAllowedError") || errStr.includes("Permission")) {
        setCameraError(
          "Camera permission denied. Please allow camera access in your browser settings."
        );
      } else if (errStr.includes("NotFoundError")) {
        setCameraError("No camera found on this device.");
      } else if (errStr.includes("NotReadableError") || errStr.includes("TrackStartError")) {
        setCameraError("Camera is in use by another app. Please close other camera apps and try again.");
      } else {
        setCameraError("Unable to start camera. Try manual entry instead.");
      }
    }
  }, [onBarcodeDetected, stopScanner]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  // Start scanner when camera mode is active and DOM is ready
  useEffect(() => {
    if (mode === "camera") {
      // Wait for the container element to be available and have dimensions.
      // We use requestAnimationFrame + a small delay to ensure the DOM has painted.
      let cancelled = false;

      const waitForContainer = () => {
        const el = document.getElementById("barcode-reader");
        if (!el || el.offsetHeight === 0) {
          // Container not ready yet, retry
          if (!cancelled) {
            requestAnimationFrame(waitForContainer);
          }
          return;
        }
        // Container is ready with dimensions, start scanning
        if (!cancelled) {
          startScanner();
        }
      };

      // Give the AnimatePresence animation a frame to start rendering
      requestAnimationFrame(() => {
        if (!cancelled) {
          // Use a short delay to let height animation begin
          setTimeout(() => {
            if (!cancelled) waitForContainer();
          }, 50);
        }
      });

      return () => {
        cancelled = true;
        stopScanner();
      };
    } else {
      stopScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ── Handle camera button tap — request permission immediately in gesture ──
  const handleCameraTap = async () => {
    setCameraError("");

    // Check if camera API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError(
        "Camera not available. Please ensure you're using HTTPS and a supported browser."
      );
      return;
    }

    // Request camera permission IMMEDIATELY in the tap handler.
    // This is critical for iOS Safari which requires getUserMedia
    // to be called in the direct user-gesture call chain.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      // Store the stream — we'll stop it once html5-qrcode takes over
      streamRef.current = stream;
      // Permission granted! Now switch to camera mode
      setMode("camera");
    } catch (err) {
      console.error("[BarcodeScanner] Permission request error:", err);
      const errStr = err?.toString?.() || "";

      if (errStr.includes("NotAllowedError") || errStr.includes("Permission")) {
        setCameraError(
          "Camera permission denied. Please allow camera access in your browser settings."
        );
      } else if (errStr.includes("NotFoundError") || errStr.includes("DevicesNotFound")) {
        setCameraError("No camera found on this device.");
      } else if (errStr.includes("NotReadableError") || errStr.includes("TrackStartError")) {
        setCameraError("Camera is in use by another app. Please close other camera apps and try again.");
      } else {
        // Try without facingMode constraint as fallback
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          streamRef.current = fallbackStream;
          setMode("camera");
          return;
        } catch (fallbackErr) {
          setCameraError("Unable to access camera. Try manual entry instead.");
        }
      }
    }
  };

  // ── Manual entry submit ────────────────────────────────────────────────────
  const handleManualSubmit = (e) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (code.length >= 8 && code.length <= 14 && /^\d+$/.test(code)) {
      onBarcodeDetected(code);
    }
  };

  const isValidBarcode =
    manualCode.trim().length >= 8 &&
    manualCode.trim().length <= 14 &&
    /^\d+$/.test(manualCode.trim());

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="w-full"
    >
      {/* ── Choose mode ─────────────────────────────────────────────────── */}
      {mode === "choose" && (
        <div className="space-y-3">
          <p className="text-sm text-slate-400 text-center mb-4">
            Scan a barcode to auto-fill nutrition data from Open Food Facts
          </p>

          {/* Scan with camera */}
          <button
            type="button"
            onClick={handleCameraTap}
            className="w-full flex items-center gap-4 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 hover:bg-indigo-500/20 hover:border-indigo-500/40 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-500/30 transition-colors">
              <svg className="w-6 h-6 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 9V6a3 3 0 013-3h3M21 9V6a3 3 0 00-3-3h-3M3 15v3a3 3 0 003 3h3M21 15v3a3 3 0 01-3 3h-3M7 12h10"
                />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">Scan with Camera</p>
              <p className="text-slate-500 text-xs mt-0.5">Point your camera at a barcode</p>
            </div>
          </button>

          {/* Camera error shown on choose screen */}
          {cameraError && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm">{cameraError}</p>
            </div>
          )}

          {/* Enter manually */}
          <button
            type="button"
            onClick={() => setMode("manual")}
            className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/15 transition-colors">
              <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">Enter Barcode Manually</p>
              <p className="text-slate-500 text-xs mt-0.5">Type the number below the barcode</p>
            </div>
          </button>
        </div>
      )}

      {/* ── Camera mode ─────────────────────────────────────────────────── */}
      {mode === "camera" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setMode("choose")}
              className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <p className="text-xs text-slate-500">Point camera at barcode</p>
          </div>

          {/* Scanner container — min-height ensures it has dimensions immediately */}
          <div
            ref={containerRef}
            className="relative rounded-xl overflow-hidden bg-black/50 border border-white/10"
            style={{ minHeight: "280px" }}
          >
            <div id="barcode-reader" className="w-full" style={{ minHeight: "280px" }} />

            {/* Scanning overlay indicator */}
            {scanning && !cameraError && (
              <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                <span className="px-3 py-1.5 bg-black/70 rounded-full text-xs text-indigo-300 font-medium flex items-center gap-2">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />
                  Scanning...
                </span>
              </div>
            )}
          </div>

          {/* Camera error */}
          {cameraError && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm">{cameraError}</p>
              <button
                type="button"
                onClick={() => setMode("manual")}
                className="mt-2 text-sm text-indigo-400 hover:text-indigo-300 font-medium"
              >
                Try manual entry instead →
              </button>
            </div>
          )}

          {/* Fallback to manual */}
          <button
            type="button"
            onClick={() => setMode("manual")}
            className="w-full text-center text-sm text-slate-500 hover:text-slate-300 py-2 transition-colors"
          >
            Or enter barcode manually
          </button>
        </div>
      )}

      {/* ── Manual mode ─────────────────────────────────────────────────── */}
      {mode === "manual" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setMode("choose")}
              className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>

          <form onSubmit={handleManualSubmit} className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1.5">
                Barcode Number
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="e.g. 8901764042270"
                autoFocus
                style={{ fontSize: "16px" }}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none font-mono tracking-widest"
              />
              <p className="text-xs text-slate-600 mt-1.5">
                Enter the 8–14 digit number printed below the barcode lines
              </p>
            </div>

            <button
              type="submit"
              disabled={!isValidBarcode || loading}
              className="w-full py-3 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-500 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Looking up...
                </>
              ) : (
                "Look Up Barcode"
              )}
            </button>
          </form>

          {/* Switch to camera */}
          <button
            type="button"
            onClick={handleCameraTap}
            className="w-full text-center text-sm text-slate-500 hover:text-slate-300 py-2 transition-colors"
          >
            Or scan with camera
          </button>
        </div>
      )}
    </motion.div>
  );
}
